/**
 * Cuál de varias aperturas abiertas es la que vale.
 *
 * El terminal tomaba `aperturasAbiertas[0]` con el comentario «la primera (la
 * más reciente)». **No era la más reciente**: `filter().toArray()` de Dexie
 * devuelve en orden de clave primaria, o sea la más ANTIGUA. El comentario
 * afirmaba lo contrario de lo que hacía el código, que es la peor clase de
 * comentario: el que impide que alguien mire.
 *
 * Y no es hipotético. Medido en el ambiente de pruebas de Ecuador el
 * 03/09/2026: de 810 aperturas, **5 cajas tenían más de una abierta a la vez**,
 * hasta tres. Con esas, el terminal elegía la más vieja y las ventas del turno
 * caían en la apertura equivocada — un turno que alguien creía cerrado.
 *
 * No se corrige la causa aquí --que existan varias abiertas es una condición de
 * los datos, no del código-- sino la consecuencia: cuál se elige deja de
 * depender del orden en que Dexie devuelva las filas.
 */

/** Cuándo empezó una apertura, para poder ordenarlas. */
const momentoDe = (apertura) => {
  const fecha = String(apertura?.FechaDeApertura ?? "").trim();
  const hora = String(apertura?.HoraDeApertura ?? "").trim();

  return `${fecha} ${hora}`.trim();
};

/**
 * La apertura abierta más reciente, con desempate estable.
 *
 * Se ordena por fecha y hora, y a igualdad gana el `id` local más alto: es el
 * contador de IndexedDB, monótono y nunca reiniciado, así que «la última que se
 * creó en este terminal» está bien definida aunque las dos compartan minuto.
 *
 * @param {Array<object>} aperturas Las que están abiertas.
 * @returns {object|null}
 */
export const laMasReciente = (aperturas) => {
  const abiertas = (aperturas ?? []).filter(Boolean);
  if (!abiertas.length) return null;

  return abiertas.reduce((mejor, candidata) => {
    const a = momentoDe(candidata);
    const b = momentoDe(mejor);
    if (a !== b) return a > b ? candidata : mejor;

    return Number(candidata.id ?? 0) > Number(mejor.id ?? 0) ? candidata : mejor;
  });
};

/**
 * Cuántas cajas tienen más de una apertura abierta, y cuáles.
 *
 * Se expone para poder avisarlo: elegir bien entre varias abiertas evita el
 * daño, pero que haya varias sigue siendo una anomalía que alguien debería
 * mirar --alguien tiene un turno que cree cerrado y no lo está--.
 *
 * @param {Array<object>} aperturas Las que están abiertas.
 * @returns {Array<{caja: string, cuantas: number}>}
 */
export const cajasConVariasAbiertas = (aperturas) => {
  const porCaja = new Map();
  for (const a of aperturas ?? []) {
    const caja = a?.Caja;
    if (!caja) continue;
    porCaja.set(caja, (porCaja.get(caja) ?? 0) + 1);
  }

  return [...porCaja.entries()]
    .filter(([, cuantas]) => cuantas > 1)
    .map(([caja, cuantas]) => ({ caja, cuantas }));
};
